import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useState } from "react";

type QuestionsProps = {
  title: string;
  answer: string;
};

type Topic = {
  name: string;
  questions: QuestionsProps[];
};

type Props = {
  heading: string;
  description: string;
  button: {
    title: string;
    variant?: "default" | "secondary";
  };
  topics: Topic[];
};

export type FaqProps = React.ComponentPropsWithoutRef<"section"> &
  Partial<Props>;

export const FaqSection = (props: FaqProps) => {
  const { heading, description, topics } = {
    ...props,
    ...FaqSectionDefaults,
  };
  const [selectedTopicIndex, setSelectedTopicIndex] = useState(0);
  const selectedTopic = topics[selectedTopicIndex];

  return (
    <section id="faq" className=" py-10 md:py-24  ">
      <div className="container ">
        <h2 className="rb-5 mb-5 text-n-primary text-3xl font-bold md:mb-6 md:text-4xl lg:text-5xl">
          {heading}
        </h2>
        <p className="md:text-lg text-n-text-neutral mb-10">{description}</p>

        <div className="grid grid-cols-1 gap-y-12 md:grid-cols-2 md:gap-x-12 lg:grid-cols-[.5fr,1fr] lg:gap-x-20">
          <div className=" self-start flex">
            <div className="mt-8 flex flex-col gap-2 p-5 border border-n-neutral-lighter rounded-xl">
              <h3 className="font-bold text-n-text mb-3">Tópicos:</h3>
              {topics.map((topic, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedTopicIndex(index)}
                  className={`text-left rounded-lg min-w-[300px] px-4 py-2  ${
                    index === selectedTopicIndex
                      ? "bg-n-primary text-white hover:bg-n-green/90 border"
                      : "bg-n-bg-1 border-none text-n-text hover:bg-gray-300/80"
                  }`}
                >
                  {topic.name}
                </button>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-2xl text-n-primary font-bold mb-4">
              {selectedTopic.name}
            </h3>
            <Accordion type="multiple" className="grid gap-4">
              {selectedTopic.questions.map((question, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="border px-5"
                >
                  <AccordionTrigger className="py-4 text-md">
                    {question.title}
                  </AccordionTrigger>
                  <AccordionContent>{question.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </div>
    </section>
  );
};

export const FaqSectionDefaults: Props = {
  heading: "Perguntas Frequentes",
  description:
    "Encontre respostas para suas dúvidas sobre nosso serviço de transporte de veículos.",
  button: {
    title: "Fale conosco",
    variant: "secondary",
  },
  topics: [
    {
      name: "1. Começando agora? Entenda como funciona o transporte de veículos",
      questions: [
        {
          title: "O que faz uma transportadora de veículos?",
          answer:
            "Uma transportadora de veículos é especializada em levar carros, motos e outros automóveis de um local para outro, com segurança, seguro e logística própria. É ideal para mudanças, compras online, viagens ou venda de veículos.",
        },
        {
          title: "Quais tipos de veículos vocês transportam?",
          answer:
            "Transportamos carros, SUVs, picapes, motos, veículos antigos, blindados, sinistrados, inoperantes e até trailers ou embarcações pequenas, desde que estejam com documentação regular.",
        },
        {
          title: "Quem pode contratar o serviço?",
          answer:
            "Atendemos tanto pessoas físicas quanto empresas, seja para o transporte de um único veículo ou frotas completas. Adaptamos o atendimento conforme a necessidade de cada cliente.",
        },
        {
          title: "Em que situações vocês costumam ser contratados?",
          answer:
            "Mudanças de cidade ou estado e compra ou venda de veículos à distância são os principais motivos. Também atendemos eventos, feiras, veículos de coleção e turismo.",
        },
        {
          title: "Vocês transportam veículos em todo o Brasil?",
          answer:
            "Sim! Atuamos em todo o território nacional, com cerca de 200 bases próprias e parceiras. Isso nos permite coletar veículos com a mesma facilidade em São Paulo ou em Boa Vista.",
        },
        {
          title: "Com quanto tempo de antecedência posso agendar o transporte?",
          answer:
            "Nas principais cidades, é possível agendar com até 24h de antecedência. Para demais localidades, o prazo é de até 48h. Atendemos mais de 3.000 cidades em todo o país.",
        },
        {
          title: "É seguro transportar meu carro com vocês?",
          answer:
            "Sim. Todos os transportes incluem seguro obrigatório contra roubo, furto e colisões dentro e fora do veículo transportador, desde a coleta até a entrega. Oferecemos ainda cobertura adicional para avarias.",
        },
        {
          title: "Quanto custa o transporte de veículos?",
          answer:
            "O valor depende da distância, tipo de veículo, modalidade e serviços adicionais. Você pode simular no primeiro e-commerce de transporte de veículos do Brasil.",
        },
        {
          title: "Quais são as modalidades de transporte disponíveis?",
          answer:
            "Oferecemos transporte convencional (cegonha compartilhada), expresso em cegonha (20% mais rápido) e expresso em guincho exclusivo (aberto ou fechado/sider).",
        },
        {
          title:
            "Qual a diferença entre contratar com a Nacional GO e uma transportadora tradicional?",
          answer:
            "Com a Nacional GO, você tem cotação instantânea, agendamento digital, acompanhamento pelo WhatsApp, cobertura superior e presença nacional com mais de 10.000 parceiros.",
        },
      ],
    },
    {
      name: "2. O que preciso saber antes de contratar o transporte",
      questions: [
        {
          title:
            "Quais documentos são necessários para contratar o transporte?",
          answer:
            "Documento de identificação com foto e comprovação de posse do veículo, como CRLV, nota fiscal ou contrato de compra e venda. Tudo pode ser enviado digitalmente.",
        },
        {
          title: "Posso colocar objetos pessoais dentro do carro?",
          answer:
            "Sim, desde que sejam declarados previamente, acondicionados em caixas lacradas e tenham o valor especificado item a item. Há restrições e taxa de serviço.",
        },
        {
          title:
            "E se o carro estiver com problemas mecânicos ou não funcionar?",
          answer:
            "Veículos inoperantes ou com panes também podem ser transportados. Basta informar no momento da cotação.",
        },
        {
          title:
            "Veículos blindados, rebaixados ou modificados podem ser transportados?",
          answer:
            "Sim. Inclui blindados com blindagem vencida ou ativa e modificações estruturais, desde que informadas com antecedência.",
        },
        {
          title: "Vocês transportam carros antigos ou de colecionadores?",
          answer:
            "Sim, com cobertura de seguro especial para veículos fora da Tabela Fipe, baseada em laudos técnicos ou valor declarado.",
        },
        {
          title: "Preciso estar presente na coleta ou entrega do veículo?",
          answer:
            "Não. Pode autorizar outra pessoa maior de 18 anos com documento de identificação.",
        },
        {
          title: "Posso escolher a data da coleta?",
          answer:
            "Sim. Com até 30 dias de antecedência ou mesmo no mesmo dia em algumas cidades. Data flexível também disponível.",
        },
        {
          title: "Posso alterar os dados do pedido depois de contratar?",
          answer:
            "Sim, desde que com no mínimo 24h de antecedência e que a alteração seja viável na logística.",
        },
        {
          title: "Como saberei que o pedido foi confirmado?",
          answer:
            "Após o pagamento, você recebe a confirmação por e-mail e WhatsApp com todos os detalhes do transporte.",
        },
        {
          title: "Existe limite de veículos por pedido?",
          answer:
            "Não. Transportamos desde veículos únicos até frotas completas, com coordenação personalizada.",
        },
        {
          title: "Posso transportar um carro comprado em nome de outra pessoa?",
          answer:
            "Sim. Basta apresentar documento que comprove a relação, como nota fiscal, contrato ou autorização assinada.",
        },
      ],
    },
    {
      name: "3. Pagamento, cancelamento e reembolso",
      questions: [
        {
          title: "Quais são as formas de pagamento aceitas?",
          answer:
            "Cartão de crédito, PIX e boleto bancário. Parcelamento em até 3x sem juros ou 12x com juros.",
        },
        {
          title: "Preciso pagar tudo no momento da contratação?",
          answer:
            "Sim. O pagamento é feito integralmente para acionarmos os parceiros e iniciar a operação.",
        },
        {
          title: "Posso cancelar meu transporte? Tenho direito a reembolso?",
          answer:
            "Sim. Cancelamentos com até 24h de antecedência têm reembolso integral. Após a coleta, não é possível cancelar.",
        },
        {
          title: "Qual é o prazo para reembolso?",
          answer:
            "Até 7 dias úteis após a solicitação formal e análise da operação.",
        },
        {
          title:
            "Posso alterar a data, endereço ou alguma informação após contratar?",
          answer:
            "Sim, desde que seja viável na logística e solicitado com antecedência.",
        },
      ],
    },
    {
      name: "4. Durante o transporte: rastreamento, prazo e coleta",
      questions: [
        {
          title: "Como funciona a coleta do veículo?",
          answer:
            "A coleta pode ser porta a porta ou em base parceira, dependendo da cidade e condições de acesso.",
        },
        {
          title: "Preciso estar presente na coleta ou na entrega?",
          answer:
            "Não é necessário. Pode autorizar outra pessoa com documento para vistoria e assinatura.",
        },
        {
          title: "Posso acompanhar o transporte do meu carro?",
          answer:
            "Sim, com atualizações por WhatsApp, e-mail ou rastreamento em tempo real em algumas rotas.",
        },
        {
          title: "Qual o prazo de entrega do veículo?",
          answer:
            "De 3 a 12 dias úteis, podendo ser 20% mais rápido no transporte expresso.",
        },
        {
          title: "Como sei que o transporte começou?",
          answer:
            "Você recebe notificação automática por WhatsApp ou e-mail com status e próximas etapas.",
        },
        {
          title: "É possível mudar o endereço de entrega durante o transporte?",
          answer:
            "Sim, se a alteração for viável na logística e informada com antecedência.",
        },
      ],
    },
    {
      name: "5. Seguro e sinistros",
      questions: [
        {
          title: "Como funciona o seguro no transporte de veículos?",
          answer:
            "Inclui cobertura básica obrigatória contra roubo, furto e colisões, além de cobertura adicional contra avarias para veículos operantes.",
        },
        {
          title: "Se eu contratar a cobertura adicional, o que está incluído?",
          answer:
            "Proteção contra qualquer tipo de avaria durante o transporte, incluindo danos por movimentação e fixação.",
        },
        {
          title: "O que acontece em casos de roubo, acidente ou perda total?",
          answer:
            "O seguro é acionado imediatamente e a indenização é finalizada em até 30 a 45 dias úteis após documentação completa.",
        },
        {
          title: "O que acontece em caso de pequenas avarias?",
          answer:
            "Devem ser comunicadas na entrega. Analisamos em até 5 dias úteis e oferecemos reparo ou reembolso.",
        },
      ],
    },
    {
      name: "6. Serviços adicionais e diferenciais",
      questions: [
        {
          title: "Posso enviar minha mudança dentro do carro?",
          answer:
            "Sim, com declaração prévia, acondicionamento adequado e pagamento de taxa. Oferecemos seguro adicional para os objetos.",
        },
        {
          title: "O que é a vistoria cautelar e quando vale a pena contratar?",
          answer:
            "É um serviço opcional que analisa procedência e condições técnicas do veículo, ideal para quem compra ou vende.",
        },
      ],
    },
    {
      name: "7. Outras dúvidas",
      questions: [
        {
          title: "Vocês oferecem transporte porta a porta?",
          answer:
            "Sim! Atendemos a maioria das cidades com coleta e entrega no endereço desejado.",
        },
        {
          title: "É possível rastrear meu veículo em tempo real?",
          answer:
            "Sim, dependendo da rota e modalidade contratada. Informe-se com nossa equipe no momento da contratação.",
        },
      ],
    },
  ],
};
